import React, { useState } from "react";
import "./Login.css";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { shouldShowOnboarding } from "./onboardingUtils";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/home";
  const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);
    setStatusMsg("Signing in...");

    // Start prefetching posts in parallel with login API call.
    // By the time login finishes (~4s), posts are already loading.
    // This hides the home page load time behind the login wait.
    let postsPrefetch = null;
    const prefetchPosts = () => {
      // We don't have a token yet, but we can warm up the connection.
      // Once login succeeds we'll use the token to fetch posts.
      postsPrefetch = new Promise((resolve) => {
        // We'll trigger this after token is received
        resolve(null);
      });
    };
    prefetchPosts();

    try {
      const response = await fetch(`${API_URL}/user/login_api`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, redirect_url: from }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user_id", String(data.user_id));
        try { sessionStorage.removeItem('posts_cache'); } catch {}

        // Start fetching posts BEFORE navigating so home renders with data
        setStatusMsg("Loading your feed...");
        try {
          const postsRes = await Promise.race([
            fetch(`${API_URL}/api/posts_home`, {
              headers: {
                'Authorization': `Bearer ${data.token}`,
                'Content-Type': 'application/json',
              }
            }).then(r => r.ok ? r.json() : null),
            // Timeout after 2s — navigate anyway, home will load posts itself
            new Promise(resolve => setTimeout(() => resolve(null), 2000))
          ]);

          if (postsRes && Array.isArray(postsRes)) {
            // Cache posts so home page renders instantly on arrival
            try { sessionStorage.setItem('posts_cache', JSON.stringify(postsRes)); } catch {}
          }
        } catch {}

        if (shouldShowOnboarding(data.user_id)) {
          navigate("/onboarding", { replace: true, state: { from } });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setError(data.error || "Invalid credentials!");
        setStatusMsg("");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("An error occurred. Please try again.");
      setStatusMsg("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setGoogleLoading(true);
    setStatusMsg("Signing in with Google...");
    try {
      const response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.needs_profile_completion) {
          navigate("/CompleteProfile", {
            state: { email: data.email, name: data.name, picture: data.picture, from }
          });
        } else {
          localStorage.setItem("token", data.token);
          localStorage.setItem("user_id", String(data.user_id));
          try { sessionStorage.removeItem('posts_cache'); } catch {}
          localStorage.setItem("user", JSON.stringify({
            email: data.email,
            name: data.name,
            picture: data.picture
          }));

          // Prefetch posts before navigating
          setStatusMsg("Loading your feed...");
          try {
            const postsRes = await Promise.race([
              fetch(`${API_URL}/api/posts_home`, {
                headers: { 'Authorization': `Bearer ${data.token}` }
              }).then(r => r.ok ? r.json() : null),
              new Promise(resolve => setTimeout(() => resolve(null), 2000))
            ]);
            if (postsRes && Array.isArray(postsRes)) {
              try { sessionStorage.setItem('posts_cache', JSON.stringify(postsRes)); } catch {}
            }
          } catch {}

          if (shouldShowOnboarding(data.user_id)) {
            navigate("/onboarding", { replace: true, state: { from } });
          } else {
            navigate(from, { replace: true });
          }
        }
      } else {
        setError(data.error || "Google login failed");
        setStatusMsg("");
        setGoogleLoading(false);
      }
    } catch (error) {
      console.error("Google login error:", error);
      setError("Failed to authenticate with Google");
      setStatusMsg("");
      setGoogleLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google login failed. Please try again.");
    setGoogleLoading(false);
  };

  if (googleLoading) {
    return (
      <div className="login-container">
        <div className="google-loading-screen">
          <img
            src="/pyate-logo-transparent.png"
            alt="Pyate logo"
            className="welcome-logo google-loading-logo"
          />
          <div className="google-loading-spinner" />
          <p className="google-loading-text">{statusMsg || "Signing in with Google..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="welcome-header">
        <div className="welcome-branding">
          <img
            src="/pyate-logo-transparent.png"
            alt="Pyate logo"
            className="welcome-logo"
          />
          <div className="welcome-text">
            <h1>Welcome to Pyate</h1>
            <p className="welcome-subtitle">Connect, support, and grow together.</p>
          </div>
        </div>
      </div>

      <div className="login-card">
        <h2 className="login-header">Welcome back</h2>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="input-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="Enter your username"
            />
          </div>
          <div className="input-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>
          {error && <p className="error-message">{error}</p>}
          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading
              ? <span>{statusMsg || "Signing in..."}</span>
              : "Sign In"
            }
          </button>
          <div className="login-options">
            <p className="forgot-password">
              <Link to="/ForgotPassword">Forgot Password?</Link>
            </p>
            <p className="login-link">
              New to Pyate? <Link to="/Register">Create account</Link>
            </p>
          </div>
        </form>

        <div className="divider">
          <span>or continue with</span>
        </div>

        <div className="third-party-login">
          <GoogleOAuthProvider clientId="1076242127532-r6qaigdph9tm626uulfntjo0hk9lpv4f.apps.googleusercontent.com">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              text="signin_with"
              shape="rectangular"
              size="large"
              width="300"
            />
          </GoogleOAuthProvider>
        </div>
      </div>
    </div>
  );
};

export default Login;